import React from "react";
import LegalLayout from "@/components/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="June 2026" testid="privacy-page">
      <p>This Privacy Policy explains how <strong>KNA, Sole Proprietor</strong> ("BidMatchAI," "we," "us") collects, uses, and protects your information when you use the BidMatchAI service (the "Service"). By using the Service, you consent to the practices described here.</p>

      <h2>1. Information We Collect</h2>
      <ul>
        <li><strong>Account information:</strong> name, email address, password (stored only as a secure hash), and login identifiers (including via Google sign-in if you choose it).</li>
        <li><strong>Business profile:</strong> company details you provide such as trade, NAICS codes, certifications, location, revenue range, bonding capacity, and similar contractor information used to generate matches and scores.</li>
        <li><strong>Payment information:</strong> processed and stored by Stripe. We receive limited billing metadata (e.g., plan, status, last four digits) but never your full card number.</li>
        <li><strong>Usage data:</strong> log data, device/browser information, and interactions with the Service (including which emailed recommendations you click) used to improve the product.</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>To provide, personalize, and improve the Service, including AI-generated readiness, qualification, and win-probability estimates.</li>
        <li>To process subscriptions, billing, and trials.</li>
        <li>To send transactional and product emails (e.g., password resets, deadline reminders, weekly digests). You can manage digest and reminder preferences in your settings.</li>
        <li>To maintain security, prevent abuse, and comply with legal obligations.</li>
      </ul>

      <h2>3. AI Processing</h2>
      <p>To generate scores, summaries, and recommendations, relevant portions of your business profile and selected public solicitation text may be sent to third-party AI model providers. These providers process the data to return results and, per their terms, do not use it to train their models on your behalf. Do not submit confidential or proprietary information you do not wish to be processed in this way.</p>

      <h2>4. How We Share Information</h2>
      <p>We do not sell your personal information. We share it only with service providers who help us operate the Service, under confidentiality obligations, including:</p>
      <ul>
        <li><strong>Stripe</strong> — payment processing.</li>
        <li><strong>Resend</strong> — transactional and product email delivery.</li>
        <li><strong>Google</strong> — optional sign-in.</li>
        <li><strong>AI model providers</strong> — to generate AI outputs as described above.</li>
        <li><strong>Hosting/infrastructure providers</strong> — to run the Service.</li>
      </ul>
      <p>We may also disclose information if required by law or to protect our rights, users, or the public.</p>

      <h2>5. Public Government Data</h2>
      <p>The Service displays publicly available data from sources such as SAM.gov, USAspending.gov, and Grants.gov. That data is subject to the terms and privacy practices of those sources, which we do not control.</p>

      <h2>6. Cookies</h2>
      <p>We use essential cookies and similar technologies to keep you signed in and to operate the Service. We may use limited analytics to understand usage. You can control cookies through your browser, though some features may not function without them.</p>

      <h2>7. Data Retention & Security</h2>
      <p>We retain your information for as long as your account is active or as needed to provide the Service and meet legal obligations. We use reasonable administrative and technical safeguards (including encryption in transit and hashed passwords) to protect your data. No method of transmission or storage is 100% secure, and we cannot guarantee absolute security.</p>

      <h2>8. Your Choices & Rights</h2>
      <p>You may access or update your profile in your dashboard, manage email preferences in settings, and request deletion of your account by emailing <a href="mailto:support@bidmatchai.io">support@bidmatchai.io</a>. Depending on your location, you may have additional rights over your personal data; contact us to exercise them.</p>

      <h2>9. Children's Privacy</h2>
      <p>The Service is intended for businesses and users 18 and older. We do not knowingly collect information from children under 13.</p>

      <h2>10. Changes to This Policy</h2>
      <p>We may update this Policy from time to time. Material changes will be posted here with a revised date and, where appropriate, notified by email.</p>

      <h2>11. Contact</h2>
      <p>Privacy questions or requests? Email <a href="mailto:support@bidmatchai.io">support@bidmatchai.io</a>.</p>
    </LegalLayout>
  );
}
